// ─────────────────────────────────────────────────────────────────
// Glass-i — Company Identity section (Stage 6RFQ · redesign 6CM.5).
// Self-contained: loads and saves the seller letterhead (name, GSTIN,
// address, contact) used on printed documents like the RFQ. Independent
// of the proforma defaults form on the same page, so each saves on its
// own. Renders inside the unified "spec sheet" as section 01 — the page
// supplies the numbered SectionHead; this component owns only the fields,
// the inline Save action, and its own load/save lifecycle + state.
// ─────────────────────────────────────────────────────────────────

import { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { Button, Card, Form, Input, Spin, message } from 'antd';
import { SaveOutlined } from '@ant-design/icons';
import { companyDefaultsApi } from './companyDefaultsApi';

// `head` is the numbered <SectionHead/> rendered by the page so the identity
// section lines up with every other section's header treatment. Exposing a
// ref lets the page show a shared "saving" affordance if it ever needs to.
const CompanyIdentityCard = forwardRef(function CompanyIdentityCard({ head }, ref) {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    companyDefaultsApi.get()
      .then((d) => {
        if (!alive || !d) return;
        form.setFieldsValue({
          companyName: d.companyName, legalName: d.legalName, gstin: d.gstin, pan: d.pan,
          addressLine1: d.addressLine1, addressLine2: d.addressLine2, city: d.city,
          state: d.state, stateCode: d.stateCode, pincode: d.pincode,
          phone: d.phone, email: d.email, contactPerson: d.contactPerson
        });
        setDirty(false);
      })
      .catch(() => { /* leave blank */ })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [form]);

  const save = async () => {
    let v; try { v = await form.validateFields(); } catch (_e) { return; }
    setSaving(true);
    try {
      await companyDefaultsApi.updateIdentity(v);
      setDirty(false);
      message.success('Company identity saved.');
    } catch (e) {
      message.error(e?.response?.data?.error?.message || e.message || 'Failed to save company identity.');
    } finally { setSaving(false); }
  };

  useImperativeHandle(ref, () => ({ save, isDirty: () => dirty }), [dirty]);

  return (
    <Card size="small" className="shadow-card cd-section" id="sec-identity" styles={{ body: { padding: 0 } }}>
      {head}
      <Spin spinning={loading}>
        <div className="px-4 py-4">
          <Form form={form} layout="vertical" requiredMark={false} onValuesChange={() => setDirty(true)}>
            {/* ── Business details ── name/legal are long → half-width; GSTIN/PAN paired ── */}
            <p className="text-[11px] font-semibold uppercase tracking-wider text-ink-400 mb-2 mt-5 first:mt-0">Business details</p>
            <div className="grid grid-cols-12 gap-x-4">
              <div className="col-span-12 md:col-span-6"><Form.Item label="Company name" name="companyName"><Input maxLength={220} placeholder="e.g., Mountain Safety Glass Pvt. Ltd." /></Form.Item></div>
              <div className="col-span-12 md:col-span-6"><Form.Item label="Legal name" name="legalName"><Input maxLength={220} placeholder="As registered on GST" /></Form.Item></div>
              <div className="col-span-12 sm:col-span-6"><Form.Item label="GSTIN" name="gstin"><Input maxLength={15} placeholder="15-char GSTIN" /></Form.Item></div>
              <div className="col-span-12 sm:col-span-6"><Form.Item label="PAN" name="pan"><Input maxLength={10} placeholder="10-char PAN" /></Form.Item></div>
            </div>

            {/* ── Registered address ── full-width lines, then a balanced city/state/code/pin row ── */}
            <p className="text-[11px] font-semibold uppercase tracking-wider text-ink-400 mb-2 mt-5 first:mt-0">Registered address</p>
            <div className="grid grid-cols-12 gap-x-4">
              <div className="col-span-12 md:col-span-6"><Form.Item label="Address line 1" name="addressLine1"><Input maxLength={220} placeholder="Building, street" /></Form.Item></div>
              <div className="col-span-12 md:col-span-6"><Form.Item label="Address line 2" name="addressLine2"><Input maxLength={220} placeholder="Area, landmark" /></Form.Item></div>
              <div className="col-span-6 md:col-span-4"><Form.Item label="City" name="city"><Input maxLength={120} placeholder="City" /></Form.Item></div>
              <div className="col-span-6 md:col-span-3"><Form.Item label="State" name="state"><Input maxLength={120} placeholder="State" /></Form.Item></div>
              <div className="col-span-6 md:col-span-2"><Form.Item label="State code" name="stateCode" rules={[{ pattern: /^[0-9]{0,2}$/, message: '2-digit code' }]}><Input maxLength={2} placeholder="33" /></Form.Item></div>
              <div className="col-span-6 md:col-span-3"><Form.Item label="Pincode" name="pincode"><Input maxLength={10} placeholder="600098" /></Form.Item></div>
            </div>

            {/* ── Contact ── three equal columns ── */}
            <p className="text-[11px] font-semibold uppercase tracking-wider text-ink-400 mb-2 mt-5 first:mt-0">Contact</p>
            <div className="grid grid-cols-12 gap-x-4">
              <div className="col-span-12 md:col-span-4"><Form.Item label="Contact person" name="contactPerson"><Input maxLength={120} placeholder="Name" /></Form.Item></div>
              <div className="col-span-12 md:col-span-4"><Form.Item label="Phone" name="phone"><Input maxLength={40} placeholder="Landline or mobile" /></Form.Item></div>
              <div className="col-span-12 md:col-span-4"><Form.Item label="Email" name="email"><Input maxLength={120} placeholder="sales@company.com" /></Form.Item></div>
            </div>
          </Form>

          <div className="flex items-center justify-between gap-3 mt-5 pt-3" style={{ borderTop: '1px solid #eef2f7' }}>
            <span className="flex items-center gap-2 text-[11.5px] text-ink-400">
              <span className="inline-block w-1.5 h-1.5 rounded-full" style={{ background: dirty ? '#f59e0b' : '#10b981' }} />
              {dirty ? 'Unsaved letterhead changes' : 'Letterhead saved'}
            </span>
            <Button type="primary" size="small" icon={<SaveOutlined />} loading={saving} onClick={save}>
              Save letterhead
            </Button>
          </div>
        </div>
      </Spin>
    </Card>
  );
});

export default CompanyIdentityCard;
